import {
  SCHEMA_VERSION,
  VERIFICATION_CAPABILITIES,
  verificationResultSchema,
  type Diagnostic,
  type IntegrityAssessment,
  type OverallStatus,
  type TrustAssessment,
  type Validity,
  type VerificationResult,
} from '@upl/evidence-schema';
import { stableEvidenceId, type ArtifactInput } from '@upl/provenance-core';
import { EvidenceMerger, type MergedEvidence } from './merger.js';
import { ExternalEvidencePolicy, type EvidenceAdapterRegistry } from './registry.js';
import {
  createAdapterExecutionResult,
  type AdapterExecutionResult,
  type AdapterMetricEvent,
  type AdapterMetricSink,
  type CanonicalBuildOptions,
  type EvidenceAdapter,
  type EvidenceAdapterContext,
  type ExecutionTarget,
} from './types.js';
import { MalformedAdapterOutputError, validateAdapterOutput } from './validation.js';

export interface VerificationOrchestratorOptions extends CanonicalBuildOptions {
  runtime: ExecutionTarget;
  timeoutMs?: number;
  policy?: ExternalEvidencePolicy;
  metricSink?: AdapterMetricSink;
  now?: () => Date;
}

export interface OrchestrationRequest {
  artifact: ArtifactInput;
  verificationId: string;
  startedAt: string;
  signal?: AbortSignal;
}

export class VerificationOrchestrator {
  readonly #timeoutMs: number;
  readonly #policy: ExternalEvidencePolicy;
  readonly #metricSink: AdapterMetricSink;
  readonly #now: () => Date;
  readonly #merger = new EvidenceMerger();

  constructor(
    private readonly registry: EvidenceAdapterRegistry,
    private readonly options: VerificationOrchestratorOptions,
  ) {
    this.#timeoutMs = options.timeoutMs ?? 15_000;
    this.#policy = options.policy ?? new ExternalEvidencePolicy();
    this.#metricSink = options.metricSink ?? (() => undefined);
    this.#now = options.now ?? (() => new Date());
  }

  async verify(request: OrchestrationRequest): Promise<VerificationResult> {
    const context: EvidenceAdapterContext = {
      artifact: request.artifact,
      verificationId: request.verificationId,
      startedAt: request.startedAt,
      runtime: this.options.runtime,
      ...(request.signal ? { signal: request.signal } : {}),
    };
    const selected: EvidenceAdapter[] = [];
    for (const adapter of this.registry.executable(this.options.runtime)) {
      if (!adapterSupports(adapter, context)) continue;
      selected.push(adapter);
      this.#emit({ name: 'adapter_selected', adapter_id: adapter.descriptor.id, category: adapter.descriptor.category, runtime: this.options.runtime });
    }
    const results = await Promise.all(selected.map((adapter) => this.#execute(adapter, context)));
    const merged = this.#merger.merge(results);
    for (const rejection of merged.rejected) {
      const source = adapterSource(rejection.adapterId, results.find(({ adapter }) => adapter.id === rejection.adapterId)?.adapter.version ?? 'unknown');
      merged.warnings.push({
        id: stableEvidenceId('warning', `merge-conflict:${rejection.adapterId}:${rejection.evidenceId}`),
        code: 'EVIDENCE_ID_CONFLICT',
        message_key: 'warnings.evidence_id_conflict',
        evidence_refs: [],
        source,
      });
    }
    return await buildCanonicalResult(
      request.artifact,
      request.verificationId,
      request.startedAt,
      this.#now().toISOString(),
      merged,
      results,
      selected.length === 0,
      this.options,
    );
  }

  async #execute(adapter: EvidenceAdapter, baseContext: EvidenceAdapterContext): Promise<AdapterExecutionResult> {
    const selectedAt = this.#now().toISOString();
    if (!this.#policy.allows(adapter.descriptor)) {
      const result = operationalFailure(adapter, selectedAt, selectedAt, selectedAt, 0, 'SKIPPED', 'NETWORK_POLICY_REJECTED', 'limitations.network_policy_rejected');
      this.#emitState(adapter, result);
      return result;
    }
    const startedAt = this.#now();
    this.#emit({ name: 'adapter_started', adapter_id: adapter.descriptor.id, category: adapter.descriptor.category, runtime: this.options.runtime });
    const controller = new AbortController();
    const abort = (): void => controller.abort();
    baseContext.signal?.addEventListener('abort', abort, { once: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeout = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new AdapterTimeoutError());
        }, this.#timeoutMs);
      });
      const inspection = Promise.resolve().then(() => adapter.inspect({ ...baseContext, signal: controller.signal }));
      inspection.catch(() => undefined);
      const raw = await Promise.race([inspection, timeout]);
      const result = validateAdapterOutput(raw);
      if (result.adapter.id !== adapter.descriptor.id || result.adapter.version !== adapter.descriptor.version) {
        throw new MalformedAdapterOutputError('Adapter identity does not match its registered descriptor');
      }
      this.#emitState(adapter, result);
      return result;
    } catch (error) {
      const completedAt = this.#now();
      const duration = Math.max(0, completedAt.getTime() - startedAt.getTime());
      const timedOut = error instanceof AdapterTimeoutError;
      const result = operationalFailure(
        adapter,
        selectedAt,
        startedAt.toISOString(),
        completedAt.toISOString(),
        duration,
        timedOut ? 'TIMED_OUT' : 'FAILED',
        timedOut ? 'ADAPTER_TIMEOUT' : error instanceof MalformedAdapterOutputError ? 'ADAPTER_OUTPUT_INVALID' : 'ADAPTER_EXECUTION_FAILED',
        timedOut ? 'limitations.adapter_timeout' : 'limitations.adapter_failure',
      );
      this.#emitState(adapter, result);
      return result;
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      baseContext.signal?.removeEventListener('abort', abort);
    }
  }

  #emitState(adapter: EvidenceAdapter, result: AdapterExecutionResult): void {
    const state = result.execution.state;
    this.#emit({
      name: state === 'TIMED_OUT' ? 'adapter_timed_out' : state === 'COMPLETED' ? 'adapter_completed' : 'adapter_failed',
      adapter_id: adapter.descriptor.id,
      category: adapter.descriptor.category,
      runtime: this.options.runtime,
      state,
    });
    this.#emit({
      name: 'adapter_evidence_count',
      adapter_id: adapter.descriptor.id,
      category: adapter.descriptor.category,
      runtime: this.options.runtime,
      count: evidenceCount(result),
    });
    this.#emit({
      name: 'adapter_duration_bucket',
      adapter_id: adapter.descriptor.id,
      category: adapter.descriptor.category,
      runtime: this.options.runtime,
      duration_bucket: durationBucket(result.execution.duration_ms),
    });
  }

  #emit(event: AdapterMetricEvent): void {
    this.#metricSink(Object.freeze(event));
  }
}

function adapterSupports(adapter: EvidenceAdapter, context: EvidenceAdapterContext): boolean {
  try {
    return adapter.supports(context);
  } catch {
    return false;
  }
}

class AdapterTimeoutError extends Error {}

function operationalFailure(
  adapter: EvidenceAdapter,
  selectedAt: string,
  startedAt: string,
  completedAt: string,
  durationMs: number,
  state: 'SKIPPED' | 'FAILED' | 'TIMED_OUT',
  code: string,
  messageKey: string,
): AdapterExecutionResult {
  const diagnostic: Diagnostic = {
    id: stableEvidenceId('limitation', `${adapter.descriptor.id}:${code}`),
    code,
    message_key: messageKey,
    evidence_refs: [],
    source: adapterSource(adapter.descriptor.id, adapter.descriptor.version),
  };
  return createAdapterExecutionResult({
    descriptor: adapter.descriptor,
    state,
    selectedAt,
    startedAt,
    completedAt,
    durationMs,
    method: 'orchestrator',
    limitations: [diagnostic],
    errors: [{ code }],
  });
}

async function buildCanonicalResult(
  input: ArtifactInput,
  verificationId: string,
  startedAt: string,
  completedAt: string,
  merged: MergedEvidence,
  results: readonly AdapterExecutionResult[],
  unsupported: boolean,
  options: CanonicalBuildOptions,
): Promise<VerificationResult> {
  const sha256 = await sha256Hex(input.bytes);
  const artifactId = stableEvidenceId('artifact', sha256);
  addUniversalLimitations(merged);
  const status = deriveStatus(merged, results, unsupported);
  const traceRefs = deriveTraceRefs(status, merged);
  const trust = deriveTrust(merged, completedAt);
  const integrity = deriveIntegrity(merged);
  const rawEntries = results
    .filter(({ execution }) => execution.state === 'COMPLETED')
    .sort((left, right) => left.adapter.id.localeCompare(right.adapter.id))
    .filter(({ source_metadata }) => Object.keys(source_metadata).length > 0)
    .map(({ adapter, source_metadata }) => [adapter.id, source_metadata] as const);
  const duration = Math.max(0, Date.parse(completedAt) - Date.parse(startedAt));
  const externalExecutions = results.filter(({ adapter, execution }) =>
    adapter.network.requiresNetwork && execution.state !== 'SKIPPED');
  const providerExecutions = externalExecutions.filter(({ adapter }) => adapter.category === 'PROVIDER');
  const mediaTransmitted = providerExecutions.some(({ adapter }) => adapter.network.mediaTransmission === 'ARTIFACT');
  const destinations = [
    ...(options.mode === 'REMOTE_EXPLICIT' ? [{
      name: 'ORIGNTD verification server',
      kind: 'ORIGNTD_SERVER' as const,
      media_transmitted: true,
      execution_state: 'COMPLETED' as const,
    }] : []),
    ...externalExecutions.map(({ adapter, execution }) => ({
      name: adapter.network.dataDestination,
      kind: 'EXTERNAL_SERVICE' as const,
      media_transmitted: adapter.network.mediaTransmission === 'ARTIFACT',
      execution_state: execution.state as 'COMPLETED' | 'FAILED' | 'TIMED_OUT',
    })),
  ];
  return verificationResultSchema.parse({
    verification_id: verificationId,
    schema_version: SCHEMA_VERSION,
    artifact: {
      id: artifactId,
      filename: input.filename,
      media_type: input.detectedMediaType,
      format: input.detectedMediaType === 'image/png' ? 'PNG' : input.detectedMediaType === 'image/jpeg' ? 'JPEG' : input.detectedMediaType,
      byte_size: input.bytes.byteLength,
      sha256,
      magic_bytes_valid: true,
    },
    overall_status: status,
    signals: merged.signals,
    credentials: merged.credentials,
    claims: merged.claims,
    assertions: merged.assertions,
    actions: merged.actions,
    actors: merged.actors,
    tools: merged.tools,
    evidence_graph: { edges: merged.edges },
    trust,
    integrity,
    conclusions: [{
      id: 'conclusion:status',
      title_key: `status.${status}.title`,
      explanation_key: `status.${status}.explanation`,
      trace_refs: traceRefs,
    }],
    warnings: [...merged.warnings].sort(byId),
    limitations: [...merged.limitations].sort(byId),
    timestamps: { verification_started_at: startedAt, verification_completed_at: completedAt },
    processing: {
      mode: options.mode,
      duration_ms: duration,
      adapters: results.map(({ adapter, processing_metadata }) => ({
        name: typeof processing_metadata.display_name === 'string' ? processing_metadata.display_name : adapter.id,
        version: typeof processing_metadata.display_version === 'string' ? processing_metadata.display_version : adapter.version,
      })).sort((left, right) => left.name.localeCompare(right.name)),
      errors: [
        ...results.flatMap(({ adapter, errors }) => errors.map(({ code }) => ({ code, adapter: adapter.id }))),
        ...merged.rejected.map(({ adapterId }) => ({ code: 'EVIDENCE_ID_CONFLICT', adapter: adapterId })),
      ].sort((left, right) => `${left.adapter}:${left.code}`.localeCompare(`${right.adapter}:${right.code}`)),
    },
    privacy: options.privacy,
    execution_disclosure: {
      local_processing: options.mode === 'LOCAL_BROWSER' || options.mode === 'LOCAL_PROCESS',
      origntd_server_processing: options.mode === 'REMOTE_EXPLICIT',
      external_provider_processing: providerExecutions.length > 0,
      media_transmitted: options.mode === 'REMOTE_EXPLICIT' || mediaTransmitted,
      destinations,
      retention: {
        origntd_source_media: options.privacy.original_media_retained ? 'RETAINED' : 'NOT_RETAINED',
        external_provider_media: mediaTransmitted ? 'NOT_ASSERTED' : 'NOT_APPLICABLE',
      },
    },
    capabilities: {
      ...VERIFICATION_CAPABILITIES,
      openai_provenance: results.some(({ adapter }) => adapter.id === 'openai-provenance' && adapter.availability === 'AVAILABLE'),
      synthid: results.some(({ adapter }) => adapter.availability === 'AVAILABLE' && adapter.evidence_methods.includes('INVISIBLE_WATERMARK')),
    },
    ...(rawEntries.length > 0 ? { raw_provider_evidence: Object.fromEntries(rawEntries) } : {}),
  });
}

function deriveStatus(merged: MergedEvidence, results: readonly AdapterExecutionResult[], unsupported: boolean): OverallStatus {
  if (unsupported) return 'UNSUPPORTED';
  if (merged.signals.some(({ state }) => state === 'INVALID') || merged.credentials.some(({ cryptographic_validity, asset_binding_validity }) => cryptographic_validity === 'INVALID' || asset_binding_validity === 'INVALID')) {
    return 'INVALID_OR_TAMPERED';
  }
  const officialSigner = merged.trustEvidence.find(({ assessment }) => assessment.policy_id === 'C2PA_OFFICIAL_TL')?.assessment;
  const trusted = officialSigner
    ? officialSigner.signer_trust === 'TRUSTED'
    : merged.trustEvidence.some(({ assessment }) => assessment.statuses.includes('TRUSTED_SIGNER'));
  const validIntegrity = merged.integrityEvidence.some(({ assessment }) => assessment.cryptographic_validity === 'VALID' && assessment.asset_binding_validity === 'VALID');
  if (trusted && validIntegrity) return 'VERIFIED_PROVENANCE';
  const supportedProvenance = merged.signals.some((signal) => {
    if (signal.kind === 'C2PA_MANIFEST') return signal.state === 'PRESENT' || signal.state === 'VALID';
    if (signal.kind === 'EMBEDDED_METADATA') return Number(signal.details.provenance_bearing_count ?? 0) > 0;
    if (signal.kind === 'PROVIDER_C2PA_DETECTION' || signal.kind === 'PROVIDER_SYNTHID_DETECTION') {
      return signal.state === 'PRESENT' || signal.state === 'VALID';
    }
    return false;
  });
  if (supportedProvenance) return 'PROVENANCE_PRESENT';
  if (results.some(({ execution }) => execution.state === 'FAILED' || execution.state === 'TIMED_OUT')) return 'INCONCLUSIVE';
  return 'NO_SUPPORTED_PROVENANCE';
}

function deriveTraceRefs(status: OverallStatus, merged: MergedEvidence): string[] {
  if (status === 'INVALID_OR_TAMPERED') {
    const invalid = merged.signals.find(({ state }) => state === 'INVALID') ?? merged.credentials.find(({ cryptographic_validity, asset_binding_validity }) => cryptographic_validity === 'INVALID' || asset_binding_validity === 'INVALID');
    if (invalid) return [invalid.id];
  }
  if (status === 'VERIFIED_PROVENANCE' || status === 'PROVENANCE_PRESENT') {
    const present = merged.signals.find(({ state }) => state === 'VALID' || state === 'PRESENT');
    if (present) return [present.id];
  }
  if (status === 'INCONCLUSIVE') {
    const diagnostic = merged.limitations.find(({ code }) => /ADAPTER_/.test(code));
    if (diagnostic) return [diagnostic.id];
  }
  const absent = merged.signals.find(({ state }) => state === 'ABSENT');
  if (absent) return [absent.id];
  return [merged.limitations[0]?.id ?? 'limitation:not-factual-truth'];
}

function deriveTrust(merged: MergedEvidence, completedAt: string): TrustAssessment {
  if (merged.trustEvidence.length === 0) {
    return {
      policy_name: 'Evidence adapter aggregation', policy_version: '1.0.0', evaluated_at: completedAt,
      statuses: ['TRUST_STATUS_UNKNOWN'], certificate_chain_validity: 'UNKNOWN', trust_list_membership: 'UNKNOWN',
    };
  }
  const assessments = merged.trustEvidence.map(({ assessment }) => assessment);
  const officialSigner = assessments.find(({ policy_id: policyId }) => policyId === 'C2PA_OFFICIAL_TL');
  const officialTimestamp = assessments.find(({ policy_id: policyId }) => policyId === 'C2PA_OFFICIAL_TSA_TL');
  const preferred = officialSigner ?? officialTimestamp ?? assessments.find(({ statuses }) => statuses.includes('TRUSTED_SIGNER')) ?? assessments[0];
  if (!preferred) throw new Error('Trust evidence disappeared during aggregation');
  const identities = [...new Set(assessments.flatMap(({ signer_identity }) => signer_identity ? [signer_identity] : []))];
  const signerStatuses = new Set<TrustAssessment['statuses'][number]>([
    'TRUSTED_SIGNER', 'KNOWN_SIGNER', 'UNKNOWN_SIGNER', 'UNTRUSTED_CHAIN',
  ]);
  const timestampStatuses = new Set<TrustAssessment['statuses'][number]>([
    'TRUSTED_TIMESTAMP', 'UNKNOWN_TIMESTAMP', 'UNTRUSTED_TIMESTAMP',
  ]);
  const statuses = assessments.flatMap((assessment) => assessment.statuses.filter((status) => {
    if (officialSigner && assessment !== officialSigner && signerStatuses.has(status)) return false;
    if (officialTimestamp && assessment !== officialTimestamp && timestampStatuses.has(status)) return false;
    return true;
  }));
  const snapshots = [...new Map(
    assessments.flatMap(({ snapshots: values }) => values ?? []).map((snapshot) => [
      `${snapshot.policy_id}:${snapshot.content_digest}`,
      snapshot,
    ]),
  ).values()];
  return {
    policy_name: preferred.policy_name,
    policy_version: preferred.policy_version,
    ...(preferred.policy_id ? { policy_id: preferred.policy_id } : {}),
    evaluated_at: preferred.evaluated_at,
    statuses: [...new Set(statuses)].sort(),
    ...(officialSigner?.signer_identity
      ? { signer_identity: officialSigner.signer_identity }
      : identities.length === 1 ? { signer_identity: identities[0] } : {}),
    certificate_chain_validity: officialSigner?.certificate_chain_validity
      ?? aggregateValidity(assessments.map(({ certificate_chain_validity }) => certificate_chain_validity)),
    trust_list_membership: officialSigner?.trust_list_membership
      ?? aggregateValidity(assessments.map(({ trust_list_membership }) => trust_list_membership)),
    ...(officialSigner?.signer_trust ? { signer_trust: officialSigner.signer_trust } : {}),
    ...(officialTimestamp?.timestamp_trust ? { timestamp_trust: officialTimestamp.timestamp_trust } : {}),
    ...(snapshots.length > 0 ? { snapshots } : {}),
  };
}

function deriveIntegrity(merged: MergedEvidence): IntegrityAssessment {
  const values = merged.integrityEvidence.map(({ assessment }) => assessment);
  if (values.length === 0) return { manifest_presence: 'UNKNOWN', cryptographic_validity: 'UNKNOWN', asset_binding_validity: 'UNKNOWN' };
  return {
    manifest_presence: values.some(({ manifest_presence }) => manifest_presence === 'PRESENT') ? 'PRESENT' : values.some(({ manifest_presence }) => manifest_presence === 'UNKNOWN') ? 'UNKNOWN' : 'ABSENT',
    cryptographic_validity: aggregateValidity(values.map(({ cryptographic_validity }) => cryptographic_validity)),
    asset_binding_validity: aggregateValidity(values.map(({ asset_binding_validity }) => asset_binding_validity)),
  };
}

function aggregateValidity(values: readonly Validity[]): Validity {
  if (values.includes('INVALID')) return 'INVALID';
  if (values.includes('VALID')) return 'VALID';
  if (values.includes('UNKNOWN')) return 'UNKNOWN';
  return 'NOT_APPLICABLE';
}

function addUniversalLimitations(merged: MergedEvidence): void {
  const values: Diagnostic[] = [
    { id: 'limitation:not-factual-truth', code: 'NOT_FACTUAL_TRUTH', message_key: 'limitations.not_factual_truth', evidence_refs: [] },
    { id: 'limitation:r1-signal-scope', code: 'R1_SIGNAL_SCOPE', message_key: 'limitations.r1_signal_scope', evidence_refs: [] },
  ];
  const ids = new Set(merged.limitations.map(({ id }) => id));
  for (const value of values) if (!ids.has(value.id)) merged.limitations.push(value);
  merged.limitations.sort(byId);
}

function adapterSource(id: string, version: string) {
  return { adapter: id, adapter_version: version, provider: id };
}

function evidenceCount(result: AdapterExecutionResult): number {
  const patch = result.evidence;
  return patch.signals.length + patch.credentials.length + patch.claims.length + patch.assertions.length + patch.actions.length + patch.actors.length + patch.tools.length + patch.trustEvidence.length + patch.integrityEvidence.length + patch.edges.length;
}

function durationBucket(duration: number): 'LT_10MS' | 'LT_100MS' | 'LT_1S' | 'LT_10S' | 'GTE_10S' {
  if (duration < 10) return 'LT_10MS';
  if (duration < 100) return 'LT_100MS';
  if (duration < 1_000) return 'LT_1S';
  if (duration < 10_000) return 'LT_10S';
  return 'GTE_10S';
}

function byId(left: { id: string }, right: { id: string }): number {
  return left.id.localeCompare(right.id);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const safeBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', safeBuffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
